import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MessageCircle, Upload, Trophy, Eye, Medal as MedalIcon, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang, imgUrl } from "@/i18n";

const CATS = ["U6", "U8", "U10", "U12", "U14"];
const EMPTY = { name: "", dob: "", nickname: "", gender: "male", shirt_size: "", favourite_team: "", jersey_number: "", position: "", school: "", parent_name: "", parent_phone: "", parent_email: "", existing_player: false, photo_path: "", scheme: 1 };

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/upload", fd);
  return r.data.path;
}

function parseCsv(text) {
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((line) => {
    const c = line.split(",").map((s) => s.trim());
    return {
      name: c[0] || "", dob: c[1] || "", gender: (c[2] || "male").toLowerCase(),
      shirt_size: c[3] || "", favourite_team: c[4] || "", jersey_number: c[5] ? Number(c[5]) : null,
      position: c[6] || "", school: c[7] || "", parent_name: c[8] || "", parent_phone: c[9] || "",
      parent_email: c[10] || "", scheme: Number(c[11]) || 1, nickname: c[12] || "", existing_player: false, photo_path: "",
    };
  });
}

export default function Players() {
  const { t } = useLang();
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [skipDeposit, setSkipDeposit] = useState(true);
  const [showLb, setShowLb] = useState(false);
  const [lb, setLb] = useState([]);
  const [profileFor, setProfileFor] = useState(null);
  const [perf, setPerf] = useState(null);
  const [medalForm, setMedalForm] = useState({ title: "", tournament: "", date: "" });
  const [leagueForm, setLeagueForm] = useState({ name: "", season: "", result: "" });
  const [fbForm, setFbForm] = useState({ text: "", rating: 5 });
  const isStaff = ["admin", "staff"].includes(user.role);

  const load = async () => {
    const r = await api.get("/players", { params: { category: cat === "all" ? "" : cat } });
    setPlayers(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, [cat]);

  useEffect(() => {
    if (!profileFor) { setPerf(null); return; }
    setPerf(null);
    api.get(`/players/${profileFor.player_id}/performance`).then((r) => setPerf(r.data)).catch((e) => toast.error(e.response?.data?.detail || t("error")));
  }, [profileFor]);

  const toggleLb = async () => {
    if (!showLb) {
      try { const r = await api.get("/players/leaderboard"); setLb(r.data); } catch { /* ignore */ }
    }
    setShowLb(!showLb);
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p, jersey_number: p.jersey_number ?? "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, jersey_number: form.jersey_number === "" ? null : Number(form.jersey_number), scheme: Number(form.scheme) || 1 };
      if (editing) await api.put(`/players/${editing.player_id}`, payload);
      else await api.post("/players", payload);
      toast.success(t("playerCreated"));
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  const submitBulk = async () => {
    const rows = parseCsv(csvText);
    if (!rows.length) { toast.error(t("bulkHint")); return; }
    setBusy(true);
    try {
      const r = await api.post("/players/bulk", { players: rows, skip_deposit: skipDeposit });
      toast.success(`${r.data.created} ${t("bulkDone")}`);
      if (r.data.errors?.length) toast.error(r.data.errors.join(" | "));
      setBulkOpen(false);
      setCsvText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  const addMedal = async (e) => {
    e.preventDefault();
    try {
      await api.post("/medals", { ...medalForm, player_id: profileFor.player_id });
      toast.success(t("saved"));
      setMedalForm({ title: "", tournament: "", date: "" });
      const r = await api.get(`/players/${profileFor.player_id}/performance`);
      setPerf(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || t("error")); }
  };

  const addLeague = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leagues", { ...leagueForm, player_id: profileFor.player_id });
      toast.success(t("saved"));
      setLeagueForm({ name: "", season: "", result: "" });
      const r = await api.get(`/players/${profileFor.player_id}/performance`);
      setPerf(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || t("error")); }
  };

  const addFeedback = async (e) => {
    e.preventDefault();
    try {
      await api.post("/feedback", { player_id: profileFor.player_id, text: fbForm.text, rating: Number(fbForm.rating) || 0 });
      toast.success(t("saved"));
      setFbForm({ text: "", rating: 5 });
      const r = await api.get(`/players/${profileFor.player_id}/performance`);
      setPerf(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || t("error")); }
  };

  const remove = async (p) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/players/${p.player_id}`);
    load();
  };

  const filtered = players.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="players-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("players")}</h1>
        <div className="flex flex-wrap gap-2">
          <Button onClick={toggleLb} data-testid="leaderboard-toggle" variant="outline" className="rounded-full">
            <Trophy size={15} className="mr-1" /> {t("leaderboard")}
          </Button>
          {isStaff && (
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button data-testid="bulk-register-btn" variant="outline" className="rounded-full border-[#165A2E]/40 text-[#165A2E]">
                  <Upload size={15} className="mr-1" /> {t("bulkRegister")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader><DialogTitle>{t("bulkRegister")}</DialogTitle></DialogHeader>
                <p className="text-xs text-slate-500">{t("bulkHint")}</p>
                <Textarea data-testid="bulk-csv" rows={8} value={csvText} onChange={(e) => setCsvText(e.target.value)}
                  placeholder="Ahmad Ali, 2015-05-10, male, M, JDT, 9, Penyerang, SK Bukit Jalil, Abu Ali, 0123456789, abu@email.com, 1" />
                <div className="flex items-center gap-3">
                  <Switch data-testid="bulk-skip-deposit" checked={skipDeposit} onCheckedChange={setSkipDeposit} />
                  <Label>{t("depositSettled")}</Label>
                </div>
                <Input type="file" accept=".csv,.txt" data-testid="bulk-file"
                  onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; const rd = new FileReader(); rd.onload = () => setCsvText(String(rd.result)); rd.readAsText(f); }} />
                <Button onClick={submitBulk} disabled={busy} data-testid="bulk-submit-btn" className="w-full rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">
                  {busy ? t("loading") : t("bulkSubmit")}
                </Button>
              </DialogContent>
            </Dialog>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openAdd} data-testid="add-player-btn" className="rounded-full bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]">
                <Plus size={16} className="mr-1" /> {t("addPlayer")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? t("editPlayer") : t("addPlayer")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label>{t("name")} *</Label>
                  <Input data-testid="player-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("nickname")}</Label>
                  <Input data-testid="player-nickname-input" value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("dob")} *</Label>
                  <Input data-testid="player-dob-input" type="date" required value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("gender")}</Label>
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger data-testid="player-gender-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">{t("male")}</SelectItem>
                      <SelectItem value="female">{t("female")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("shirtSize")}</Label>
                  <Input data-testid="player-shirt-input" value={form.shirt_size} onChange={(e) => setForm({ ...form, shirt_size: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("favTeam")}</Label>
                  <Input data-testid="player-team-input" value={form.favourite_team} onChange={(e) => setForm({ ...form, favourite_team: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("jersey")}</Label>
                  <Input data-testid="player-jersey-input" type="number" value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("position")}</Label>
                  <Input data-testid="player-position-input" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("school")}</Label>
                  <Input data-testid="player-school-input" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("scheme")}</Label>
                  <Select value={String(form.scheme || 1)} onValueChange={(v) => setForm({ ...form, scheme: Number(v) })} disabled={user.role === "parent"}>
                    <SelectTrigger data-testid="player-scheme-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">{t("scheme1")}</SelectItem>
                      <SelectItem value="2">{t("scheme2")}</SelectItem>
                      <SelectItem value="3">{t("scheme3")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("photo")}</Label>
                  <Input data-testid="player-photo-input" type="file" accept="image/*" className="mt-1"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try { const path = await uploadFile(f); setForm((prev) => ({ ...prev, photo_path: path })); toast.success(t("saved")); }
                      catch { toast.error(t("error")); }
                    }} />
                  {form.photo_path && <img src={imgUrl(form.photo_path)} alt="" className="mt-2 h-20 w-20 rounded-lg object-cover" data-testid="player-photo-preview" />}
                </div>
                <div className="sm:col-span-2 mt-2 border-t pt-4">
                  <p className="mb-3 text-sm font-semibold text-[#165A2E]">{t("parentInfo")}</p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <Label>{t("parentName")}</Label>
                      <Input data-testid="player-parent-name" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label>{t("parentPhone")}</Label>
                      <Input data-testid="player-parent-phone" placeholder="0123456789" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label>{t("parentEmail")}</Label>
                      <Input data-testid="player-parent-email" type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="mt-1" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:col-span-2">
                  <Switch data-testid="player-existing-toggle" checked={form.existing_player} onCheckedChange={(v) => setForm({ ...form, existing_player: v })} />
                  <Label className="cursor-pointer">{t("existingPlayer")}</Label>
                </div>
                <div className="sm:col-span-2">
                  <Button data-testid="player-submit-btn" disabled={busy} className="w-full rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">
                    {busy ? t("loading") : t("save")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {showLb && (
        <div className="gfa-card overflow-x-auto" data-testid="leaderboard-panel">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("ranking")}</TableHead>
                <TableHead>{t("name")}</TableHead>
                <TableHead>{t("category")}</TableHead>
                <TableHead>{t("goals")}</TableHead>
                <TableHead>{t("assists")}</TableHead>
                <TableHead>{t("matchesPlayed")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lb.map((r) => (
                <TableRow key={r.player_id} data-testid={`lb-row-${r.player_id}`}>
                  <TableCell className="font-['Outfit'] text-lg font-bold text-[#165A2E]">#{r.rank}</TableCell>
                  <TableCell className="font-medium">{r.name}{r.jersey_number ? ` (#${r.jersey_number})` : ""}</TableCell>
                  <TableCell>{r.category}</TableCell>
                  <TableCell className="font-bold">{r.goals}</TableCell>
                  <TableCell>{r.assists}</TableCell>
                  <TableCell>{r.matches}</TableCell>
                </TableRow>
              ))}
              {lb.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-400">{t("noData")}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Input data-testid="player-search" placeholder={t("searchPlayer")} value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="player-category-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("categoryAll")}</SelectItem>
            {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="gfa-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("photo")}</TableHead>
              <TableHead>{t("name")}</TableHead>
              <TableHead>{t("category")}</TableHead>
              <TableHead>{t("age")}</TableHead>
              <TableHead>{t("jersey")}</TableHead>
              <TableHead>{t("position")}</TableHead>
              <TableHead>{t("scheme")}</TableHead>
              <TableHead>{t("parentPhone")}</TableHead>
              <TableHead>{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.player_id} data-testid={`player-row-${p.player_id}`}>
                <TableCell>
                  {p.photo_path ? (
                    <img src={imgUrl(p.photo_path)} alt="" className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#165A2E]/10 font-bold text-[#165A2E]">{p.name[0]}</div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell><span className="rounded-full bg-[#84CC16]/15 px-2.5 py-0.5 text-xs font-bold text-[#165A2E]">{p.category}</span></TableCell>
                <TableCell>{p.age}</TableCell>
                <TableCell>{p.jersey_number ?? "-"}</TableCell>
                <TableCell>{p.position || "-"}</TableCell>
                <TableCell className="text-xs">S{p.scheme || 1}</TableCell>
                <TableCell>
                  {p.parent_phone ? (
                    <a href={`https://wa.me/${p.parent_phone.replace(/\D/g, "").replace(/^0/, "60")}`} target="_blank" rel="noreferrer" data-testid={`player-wa-${p.player_id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-[#25D366] hover:underline">
                      <MessageCircle size={14} /> {p.parent_phone}
                    </a>
                  ) : "-"}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setProfileFor(p)} data-testid={`view-player-${p.player_id}`} className="text-[#165A2E]"><Eye size={15} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-player-${p.player_id}`}><Pencil size={15} /></Button>
                    {isStaff && (
                      <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`delete-player-${p.player_id}`} className="text-red-500"><Trash2 size={15} /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={9} className="py-8 text-center text-slate-400">{t("noData")}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!profileFor} onOpenChange={(v) => !v && setProfileFor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          {!perf ? (
            <p className="py-8 text-center text-slate-400">{t("loading")}</p>
          ) : (
            <div className="space-y-6" data-testid="player-profile">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-4">
                  {perf.player.photo_path ? (
                    <img src={imgUrl(perf.player.photo_path)} alt="" className="h-14 w-14 rounded-full border-2 border-[#84CC16] object-cover" />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#165A2E] font-['Outfit'] text-xl font-bold text-[#84CC16]">{perf.player.name[0]}</div>
                  )}
                  <div>
                    <p>{perf.player.name}{perf.player.nickname ? ` "${perf.player.nickname}"` : ""}</p>
                    <p className="text-sm font-normal text-slate-500">
                      {perf.player.category} · {t("age")} {perf.player.age} · {perf.player.gender === "male" ? t("male") : t("female")} · {t(`scheme${perf.player.scheme || 1}`)}
                    </p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">{t("jersey")}</p><p className="font-semibold">{perf.player.jersey_number ?? "-"}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">{t("position")}</p><p className="font-semibold">{perf.player.position || "-"}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">{t("school")}</p><p className="font-semibold">{perf.player.school || "-"}</p></div>
                <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">{t("favTeam")}</p><p className="font-semibold">{perf.player.favourite_team || "-"}</p></div>
              </div>

              <div>
                <p className="mb-2 font-['Outfit'] font-bold text-[#165A2E]">{t("performance")}</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="gfa-card p-4 text-center"><p className="font-['Outfit'] text-2xl font-bold text-[#165A2E]" data-testid="perf-goals">{perf.goals}</p><p className="text-xs text-slate-500">{t("goals")}</p></div>
                  <div className="gfa-card p-4 text-center"><p className="font-['Outfit'] text-2xl font-bold text-[#165A2E]">{perf.assists}</p><p className="text-xs text-slate-500">{t("assists")}</p></div>
                  <div className="gfa-card p-4 text-center"><p className="font-['Outfit'] text-2xl font-bold text-[#165A2E]">{perf.matches}</p><p className="text-xs text-slate-500">{t("matchesPlayed")}</p></div>
                </div>
              </div>

              <div>
                <p className="mb-2 font-['Outfit'] font-bold text-[#165A2E]">{t("attendanceRate")}</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 p-4" data-testid="perf-training-rate">
                    <p className="text-sm text-slate-500">{t("trainingJoined")}</p>
                    <p className="font-['Outfit'] text-xl font-bold text-[#165A2E]">{perf.training_attended}/{perf.training_total}
                      {perf.training_total > 0 && <span className="ml-2 text-sm text-[#84CC16]">{Math.round((perf.training_attended / perf.training_total) * 100)}%</span>}
                    </p>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-4" data-testid="perf-match-rate">
                    <p className="text-sm text-slate-500">{t("matchesJoined")}</p>
                    <p className="font-['Outfit'] text-xl font-bold text-[#165A2E]">{perf.matches_attended}/{perf.matches_total}
                      {perf.matches_total > 0 && <span className="ml-2 text-sm text-[#84CC16]">{Math.round((perf.matches_attended / perf.matches_total) * 100)}%</span>}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-2 font-['Outfit'] font-bold text-[#165A2E]">{t("sessionHistory")}</p>
                {perf.history.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> : (
                  <div className="max-h-48 space-y-2 overflow-y-auto">
                    {perf.history.map((h, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 text-sm" data-testid={`history-item-${i}`}>
                        <span>{h.title} <span className="text-xs text-slate-400">· {h.date} · {["training", "school"].includes(h.type) ? t("training") : t("match")}</span></span>
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${h.present ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                          {h.present ? t("present") : t("absent")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="mb-2 flex items-center gap-2 font-['Outfit'] font-bold text-[#165A2E]"><Flag size={16} /> {t("matchesList")}</p>
                {perf.match_stats.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> : (
                  <div className="space-y-2">
                    {perf.match_stats.map((m, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 text-sm" data-testid={`perf-match-${i}`}>
                        <span>{m.schedule_title} <span className="text-xs text-slate-400">· {m.date}</span></span>
                        <span className="font-semibold text-[#165A2E]">{m.goals} {t("goals")} · {m.assists} {t("assists")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="mb-2 flex items-center gap-2 font-['Outfit'] font-bold text-[#165A2E]"><Trophy size={16} /> {t("leagues")}</p>
                {perf.leagues.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> : (
                  <div className="space-y-2">
                    {perf.leagues.map((l) => (
                      <div key={l.league_id} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 text-sm" data-testid={`perf-league-${l.league_id}`}>
                        <span className="font-medium">{l.name} <span className="text-xs text-slate-400">· {l.season}</span></span>
                        <span className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-amber-600">{l.result}</span>
                          {user.role === "admin" && (
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" data-testid={`del-league-${l.league_id}`}
                              onClick={async () => { await api.delete(`/leagues/${l.league_id}`); const r = await api.get(`/players/${profileFor.player_id}/performance`); setPerf(r.data); }}>
                              <Trash2 size={12} />
                            </Button>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {isStaff && (
                  <form onSubmit={addLeague} className="mt-3 grid grid-cols-1 gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-4">
                    <Input data-testid="league-name" placeholder={t("leagueName")} required value={leagueForm.name} onChange={(e) => setLeagueForm({ ...leagueForm, name: e.target.value })} />
                    <Input data-testid="league-season" placeholder={t("season")} value={leagueForm.season} onChange={(e) => setLeagueForm({ ...leagueForm, season: e.target.value })} />
                    <Input data-testid="league-result" placeholder={t("result")} value={leagueForm.result} onChange={(e) => setLeagueForm({ ...leagueForm, result: e.target.value })} />
                    <Button data-testid="league-add-btn" className="rounded-full bg-[#165A2E] text-xs text-white">{t("addLeague")}</Button>
                  </form>
                )}
              </div>

              <div>
                <p className="mb-2 flex items-center gap-2 font-['Outfit'] font-bold text-[#165A2E]"><MedalIcon size={16} /> {t("medals")}</p>
                {perf.medals.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> : (
                  <div className="flex flex-wrap gap-2">
                    {perf.medals.map((m) => (
                      <span key={m.medal_id} className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-1.5 text-xs font-semibold text-amber-700" data-testid={`perf-medal-${m.medal_id}`}>
                        <MedalIcon size={12} /> {m.title}{m.tournament ? ` · ${m.tournament}` : ""}{m.date ? ` · ${m.date}` : ""}
                        {user.role === "admin" && (
                          <button className="text-red-400 hover:text-red-600" data-testid={`del-medal-${m.medal_id}`}
                            onClick={async () => { await api.delete(`/medals/${m.medal_id}`); const r = await api.get(`/players/${profileFor.player_id}/performance`); setPerf(r.data); }}>×</button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
                {isStaff && (
                  <form onSubmit={addMedal} className="mt-3 grid grid-cols-1 gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-4">
                    <Input data-testid="medal-title" placeholder={t("medalTitle")} required value={medalForm.title} onChange={(e) => setMedalForm({ ...medalForm, title: e.target.value })} />
                    <Input data-testid="medal-tournament" placeholder={t("tournament")} value={medalForm.tournament} onChange={(e) => setMedalForm({ ...medalForm, tournament: e.target.value })} />
                    <Input data-testid="medal-date" type="date" value={medalForm.date} onChange={(e) => setMedalForm({ ...medalForm, date: e.target.value })} />
                    <Button data-testid="medal-add-btn" className="rounded-full bg-[#165A2E] text-xs text-white">{t("addMedal")}</Button>
                  </form>
                )}
              </div>
              <div>
                <p className="mb-2 font-['Outfit'] font-bold text-[#165A2E]">{t("feedback")}</p>
                {perf.feedback.length === 0 ? <p className="text-sm text-slate-400">{t("noData")}</p> : (
                  <div className="space-y-2">
                    {perf.feedback.map((f) => (
                      <div key={f.feedback_id} className="rounded-lg border border-slate-100 p-3" data-testid={`feedback-${f.feedback_id}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-[#165A2E]">{f.coach_name}{f.rating ? ` · ${f.rating}/5` : ""}</p>
                          <span className="flex items-center gap-2 text-xs text-slate-400">
                            {new Date(f.created_at).toLocaleDateString()}
                            {user.role === "admin" && (
                              <button className="text-red-400 hover:text-red-600" data-testid={`del-feedback-${f.feedback_id}`}
                                onClick={async () => { await api.delete(`/feedback/${f.feedback_id}`); const r = await api.get(`/players/${profileFor.player_id}/performance`); setPerf(r.data); }}>×</button>
                            )}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-slate-600">{f.text}</p>
                      </div>
                    ))}
                  </div>
                )}
                {isStaff && (
                  <form onSubmit={addFeedback} className="mt-3 grid grid-cols-1 gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-[1fr_110px_auto]">
                    <Input data-testid="feedback-text" placeholder={t("feedbackPlaceholder")} required value={fbForm.text} onChange={(e) => setFbForm({ ...fbForm, text: e.target.value })} />
                    <Input data-testid="feedback-rating" type="number" min="0" max="5" placeholder={t("rating")} value={fbForm.rating} onChange={(e) => setFbForm({ ...fbForm, rating: e.target.value })} />
                    <Button data-testid="feedback-add-btn" className="rounded-full bg-[#165A2E] text-xs text-white">{t("addFeedback")}</Button>
                  </form>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
